// App.js
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from './_Components/AuthContext';
import { ThemeProvider } from './_Themes/_ThemeProvider';
import { Box, CircularProgress } from '@mui/material';

// Registration
const Signup = lazy(() => import('./_Registration/SignUp'));
const Login = lazy(() => import('./_Registration/SignIn'));

// Main
const Dashboards = lazy(() => import('./_Main/Dashboards'));
const Page = lazy(() => import('./_Main/Page'));
const MarketNews = lazy(() => import('./Dashboard/MarketNews'));
const WatchList = lazy(() => import('./Dashboard/WatchList'));
const AddStock = lazy(() => import('./Dashboard/AddStock'));
const UpdateProfile = lazy(() => import('./UpdateProfile'));
const LightWeightChartSelf = lazy(() => import('./charts/LightWeightChart'));

// Side /
const SideBar= lazy(() => import('./_Side/SideBar'));
const News = lazy(() => import('./_Side/News'));
const Market = lazy(() => import('./_Side/Market'));
const Offers = lazy(() => import('./_Side/Offers'));
const Discover = lazy(() => import('./_Side/Discover'));
// Side / _HR
const Recruitment = lazy(() => import('./_Side/_hr/Recruitment'));
const CompensationBenefits = lazy(() => import('./_Side/_hr/CompensationBenefits'));
const EmployeeRelations = lazy(() => import('./_Side/_hr/EmployeeRelations'));
const HRAnalytics = lazy(() => import('./_Side/_hr/HRAnalytics'));
const TrainingDevelopment = lazy(() => import('./_Side/_hr/TrainingDevelopment'));
// Side / _CustomerRelationship
const LeadsManagement = lazy(() => import('./_Side/_CustomerRelationship/LeadsManagement'));
const CustomerSupport = lazy(() => import('./_Side/_CustomerRelationship/CustomerSupport'));
const CustomerFeedback = lazy(() => import('./_Side/_CustomerRelationship/CustomerFeedback'));
const SalesPipeline = lazy(() => import('./_Side/_CustomerRelationship/SalesPipeline'));
  // Side / _Procurement
const VendorManagement = lazy(() => import('./_Side/_Procurement/VendorManagement'));
const PurchaseOrders = lazy(() => import('./_Side/_Procurement/PurchaseOrders'));
const SupplierEvaluation = lazy(() => import('./_Side/_Procurement/SupplierEvaluation'));
 // Side / _Sales
const SalesStrategy = lazy(() => import('./_Side/_Sales/SalesStrategy'));
const PerformanceTracking = lazy(() => import('./_Side/_Sales/PerformanceTracking'));
const CustomerAccounts = lazy(() => import('./_Side/_Sales/CustomerAccounts'));
// Side / _Inventory
const StockControl = lazy(() => import('./_Side/_Inventory/StockControl'));
const Warehousing = lazy(() => import('./_Side/_Inventory/Warehousing'));
const InventoryAudits = lazy(() => import('./_Side/_Inventory/InventoryAudits'));
// Side / _Production
const ProductionPlanning = lazy(() => import('./_Side/_Production/ProductionPlanning'));
const QualityControl = lazy(() => import('./_Side/_Production/QualityControl'));
const ProcessOptimization = lazy(() => import('./_Side/_Production/ProcessOptimization'));
 // Side / _Shipping
const LogisticsScheduling = lazy(() => import('./_Side/_Shipping/LogisticsScheduling'));
const ShippingTracking = lazy(() => import('./_Side/_Shipping/ShippingTracking'));
const DeliveryPerformance = lazy(() => import('./_Side/_Shipping/DeliveryPerformance'));
 // Side / _Financial
const Budgeting = lazy(() => import('./_Side/_Financial/Budgeting'));
const Accounting = lazy(() => import('./_Side/_Financial/Accounting'));
const FinancialReporting = lazy(() => import('./_Side/_Financial/FinancialReporting'));
 // Side / _Asset
const AssetTracking = lazy(() => import('./_Side/_Asset/AssetTracking'));
const MaintenanceScheduling = lazy(() => import('./_Side/_Asset/MaintenanceScheduling'));
const DepreciationManagement = lazy(() => import('./_Side/_Asset/DepreciationManagement'));
 // Side / _SupplyChain
const LogisticsManagement = lazy(() => import('./_Side/_SupplyChain/LogisticsManagement'));
const InventoryCoordination = lazy(() => import('./_Side/_SupplyChain/InventoryCoordination'));
const SupplierCollaboration = lazy(() => import('./_Side/_SupplyChain/SupplierCollaboration'));
 // Side / _Quality
const QualityAssurance = lazy(() => import('./_Side/_Quality/QualityAssurance'));
const ProcessAudits = lazy(() => import('./_Side/_Quality/ProcessAudits'));
const ComplianceMonitoring = lazy(() => import('./_Side/_Quality/ComplianceMonitoring'));
 // Side / _Innovation
const RDManagement = lazy(() => import('./_Side/_Innovation/RDManagement'));
const ProductInnovation = lazy(() => import('./_Side/_Innovation/ProductInnovation'));
const ProcessInnovation = lazy(() => import('./_Side/_Innovation/ProcessInnovation'));
 // Side / _Risk
const RiskAssessment = lazy(() => import('./_Side/_Risk/RiskAssessment'));
const MitigationPlanning = lazy(() => import('./_Side/_Risk/MitigationPlanning'));
const ComplianceRisk = lazy(() => import('./_Side/_Risk/ComplianceRisk'));



 // _Top /
const Settings = lazy(() => import('./_Top/Settings'));
const About = lazy(() => import('./_Top/_About'));
const Global = lazy(() => import('./_Top/_Blog'));
const Navigation = lazy(() => import('./_Top/_Navigation'));
const Community = lazy(() => import('./_Top/_Community'));
const Ai = lazy(() => import('./_Top/_Ai'));

// Down / Planner
const DailyPlanner = lazy(() => import('./_Down/_Planner/_DailyPlanner'));

const PrivateRoute = () => {
  const { currentUser } = useAuth();
  return currentUser ? <Outlet /> : <Navigate to="/login" />;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        }>
          <Routes>

            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />

            <Route element={<PrivateRoute />}>
              <Route path="/page" element={<Page />} />
              <Route path="/update-profile" element={<UpdateProfile />} />

              {/* DASHBOARD */}
              <Route path="/dashboard/*" element={<Dashboards />}>

                {/* Default Home */}
                <Route index element={<MarketNews />} />

                {/* Dashboard pages */}
                <Route path="watchlist" element={<WatchList />} />
                <Route path="addstock" element={<AddStock />} />
                <Route path="lightWeight" element={<LightWeightChartSelf />} />

                   {/* Side pages */}
                <Route path="sideDrawer" element={<SideBar/>} />
                <Route path="settings" element={<Settings />} />
                <Route path="news" element={<News />} />
                <Route path="market" element={<Market />} />
                <Route path="offers" element={<Offers />} />
                <Route path="discover" element={<Discover />} />
                   {/* Side pages _HR*/}
                 <Route path="recruitment" element={<Recruitment />} />
                 <Route path="compensation-benefits" element={<CompensationBenefits />} />
                 <Route path="employee-relations" element={<EmployeeRelations />} />
                 <Route path="hr-analytics" element={<HRAnalytics />} />
                 <Route path="training-development" element={<TrainingDevelopment />} />
                  {/* Side pages _CustomerRelationship*/}
                 <Route path="leads-management" element={<LeadsManagement />} />
                 <Route path="customer-support" element={<CustomerSupport />} />
                 <Route path="customer-feedback" element={<CustomerFeedback />} />
                 <Route path="sales-pipeline" element={<SalesPipeline />} />
                     {/* Side pages _Procurement*/}
                 <Route path="vendor-management" element={<VendorManagement />} />
                 <Route path="purchase-orders" element={<PurchaseOrders />} />
                 <Route path="supplier-evaluation" element={<SupplierEvaluation />} />
                      {/* Side pages _Sales*/}
                 <Route path="sales-strategy" element={<SalesStrategy />} />
                 <Route path="performance-tracking" element={<PerformanceTracking />} />
                 <Route path="customer-accounts" element={<CustomerAccounts />} />
                      {/* Side pages _Inventory*/}
                 <Route path="stock-control" element={<StockControl />} />
                 <Route path="warehousing" element={<Warehousing />} />
                 <Route path="inventory-audits" element={<InventoryAudits />} />
                      {/* Side pages _Production*/}
                 <Route path="production-planning" element={<ProductionPlanning />} />
                 <Route path="quality-control" element={<QualityControl />} />
                 <Route path="process-optimization" element={<ProcessOptimization />} />
                      {/* Side pages _Shipping*/}
                 <Route path="logistics-scheduling" element={<LogisticsScheduling />} />
                 <Route path="shipping-tracking" element={<ShippingTracking />} />
                 <Route path="delivery-performance" element={<DeliveryPerformance />} />
                      {/* Side pages _Financial*/}
                 <Route path="budgeting" element={<Budgeting />} />
                 <Route path="accounting" element={<Accounting />} />
                 <Route path="financial-reporting" element={<FinancialReporting />} />
                      {/* Side pages _Asset*/}
                 <Route path="asset-tracking" element={<AssetTracking />} />
                 <Route path="maintenance-scheduling" element={<MaintenanceScheduling />} />
                 <Route path="depreciation-management" element={<DepreciationManagement />} />
                      {/* Side pages _SupplyChain*/}
                 <Route path="logistics-management" element={<LogisticsManagement />} />
                 <Route path="inventory-coordination" element={<InventoryCoordination />} />
                 <Route path="supplier-collaboration" element={<SupplierCollaboration />} />
                      {/* Side pages _Quality*/}
                 <Route path="quality-assurance" element={<QualityAssurance />} />
                 <Route path="process-audits" element={<ProcessAudits />} />
                 <Route path="compliance-monitoring" element={<ComplianceMonitoring />} />
                      {/* Side pages _Innovation*/}
                 <Route path="rnd-management" element={<RDManagement />} />
                 <Route path="product-innovation" element={<ProductInnovation />} />
                 <Route path="process-innovation" element={<ProcessInnovation />} />
                      {/* Side pages _Risk*/}
                 <Route path="risk-assessment" element={<RiskAssessment />} />
                 <Route path="mitigation-planning" element={<MitigationPlanning />} />
                 <Route path="compliance-risk" element={<ComplianceRisk />} />



                {/* TOP pages */}
                <Route path="about" element={<About />} />
                <Route path="global" element={<Global />} />
                <Route path="navigation" element={<Navigation />} />
                <Route path="community" element={<Community />} />
                <Route path="ai" element={<Ai />} />
                {/* DOWN / PLANNER pages */}
                <Route path="planner/daily" element={<DailyPlanner />} />

              </Route>
            </Route>

          </Routes>
        </Suspense>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
